import { defineStore } from "pinia";
import { ref, markRaw } from "vue";
import type { DefineComponent, EmitsOptions, PropType, Raw } from "vue";

import type { ViewportCoordinate } from "@/model/coordinates";
import { setContextMenuOpen } from "@/utils/shortcut/shortcuts";

/* eslint-disable @typescript-eslint/no-explicit-any */
export type VueComponent<PropTypes, E extends EmitsOptions = Record<string, any>> = DefineComponent<
  PropTypes,
  object,
  unknown,
  Record<string, any>,
  Record<string, any>,
  Record<string, any>,
  Record<string, any>,
  E
>;
/* eslint-enable */

type RequiredProps<T> = { [K in keyof T]: { type: PropType<T[K]>; required: true } };

interface ContextMenu {
  component: Raw<VueComponent<unknown>>;
  props: ContextMenuProps;
  eventHandlers?: EventHandlers;
}

export interface ContextMenuProps {
  position: ViewportCoordinate;
}

export type EventHandlers = Record<string, (...event: never[]) => void | Promise<void>> & {
  close?: () => void | Promise<void>;
};

export const useContextMenuStore = defineStore("contextMenu", () => {
  const menu = ref<ContextMenu | null>(null);
  let nextMenu: ContextMenu | null = null;

  function open<T extends ContextMenuProps, E extends EmitsOptions>(
    component: VueComponent<RequiredProps<T>, E>,
    props: T,
    eventHandlers?: EventHandlers,
  ) {
    const newMenu = {
      component: markRaw(component) as Raw<VueComponent<unknown>>,
      props,
      eventHandlers,
    };
    if (!menu.value) {
      menu.value = newMenu;
    } else {
      nextMenu = newMenu;
    }
    setContextMenuOpen(true);
  }

  function close() {
    menu.value = null;
    if (nextMenu) {
      menu.value = nextMenu;
      nextMenu = null;
    } else {
      setContextMenuOpen(false);
    }
  }

  return { menu, open, close };
});
